import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../utils/constants';
import { getAuthToken } from '../utils/storage';

/**
 * Global inventory settings, read once per session and cached.
 *
 * Mirrors PrintSettingsContext. Readable by every role because the whole coil UI is
 * gated on it; only a super admin can change it (enforced by the API).
 *
 * ONE DELIBERATE DIFFERENCE from PrintSettingsContext: this one fails CLOSED.
 * PrintSettings falls back to what challans already printed on, so a failed fetch is
 * harmless. Coil tracking is not symmetric — if the UI wrongly believes the feature is
 * ON while the backend has it OFF, the operator picks specific coils on an order and
 * the backend honours none of them: no coil is held, no remainder is created, and the
 * picks are silently dropped. Wrongly believing it is OFF only hides a feature. So an
 * unreachable or erroring endpoint leaves this false.
 */

const api = axios.create({ baseURL: `${API_BASE_URL}/api/v1` });
api.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const CACHE_TTL_MS = 60 * 1000;

// The only inventory-settings key the UI reads today.
const COIL_TRACKING_KEY = 'coil_tracking';

const CoilSettingsContext = createContext(null);

export const CoilSettingsProvider = ({ children }) => {
  const [coilTrackingEnabled, setCoilTrackingEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const lastFetchRef = useRef(0);

  const fetchSettings = useCallback(async (force = false) => {
    if (!force && Date.now() - lastFetchRef.current < CACHE_TTL_MS) return;
    try {
      const res = await api.get('/inventory-settings');
      // A missing row reads as disabled — same fail-closed rule as an error.
      const row = (res.data || []).find((item) => item?.key === COIL_TRACKING_KEY);
      setCoilTrackingEnabled(Boolean(row?.enabled));
      lastFetchRef.current = Date.now();
    } catch {
      // Fail closed: hide the feature rather than let an order be picked against a
      // backend that will ignore the picks. lastFetchRef is left untouched so the
      // next read retries instead of caching the failure for 60s.
      setCoilTrackingEnabled(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSettings(true); }, [fetchSettings]);

  /**
   * Toggle the global coil-tracking setting. Super admin only (enforced by the API,
   * which rejects everyone else — let the caller surface that error).
   *
   * @param {boolean} enabled
   */
  const updateCoilSetting = useCallback(async (enabled) => {
    const res = await api.put(`/inventory-settings/${COIL_TRACKING_KEY}`, { enabled });
    setCoilTrackingEnabled(Boolean(res.data?.enabled));
    lastFetchRef.current = 0; // invalidate cache
    return res.data;
  }, []);

  return (
    <CoilSettingsContext.Provider value={{
      coilTrackingEnabled,
      loading,
      updateCoilSetting,
      refetch: () => fetchSettings(true),
    }}>
      {children}
    </CoilSettingsContext.Provider>
  );
};

export const useCoilSettings = () => {
  const ctx = useContext(CoilSettingsContext);
  if (!ctx) throw new Error('useCoilSettings must be used inside CoilSettingsProvider');
  return ctx;
};
